/**
 * BonusStampModal — L4 (2026-06-25) shared grant modal.
 * ======================================================
 *
 * Powers two flows against the L2 backend endpoints:
 *   • Mode `on_transaction`  → POST /api/owner/transactions/{id}/bonus-stamp
 *   • Mode `standalone`      → POST /api/owner/customers/{id}/bonus-stamp
 *
 * Contract locked by L2 (see /app/backend/bonus_stamps.py):
 *   idempotency_key  – client uuid, REUSED across retries so a
 *                      network-blip retry is a no-op cache hit.
 *   amount           – strictly positive int; no upper cap.
 *   source           – 8-value enum.
 *   reason           – non-blank free text ≤ 500 chars.
 *   notes            – optional free text ≤ 1000 chars.
 *
 * Design invariants:
 *   - RBAC visibility is caller responsibility (`BonusStampButton`
 *     helper below already checks). We assert here defensively.
 *   - `auto_stamp` is READ-ONLY on every UI surface — we never
 *     display an editable field for it (D6).
 *   - On 200 we surface a toast + `onGranted(response)` so the caller
 *     can update its local state / invalidate its query cache without
 *     us knowing which cache keys apply.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Lock, Info, Sparkles } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuthStore } from '@/lib/auth';
import { ROLES } from '@/lib/rbac';
import { toast } from '@/hooks/use-toast';
import Modal from '@/components/ui_primitives/Modal';
import Button from '@/components/ui_primitives/Button';
import { Input, Textarea, Select, FieldRow } from '@/components/ui_primitives/Field';

// D8 source enum — labels are the friendly copy shown in the dropdown;
// values are the exact backend enum strings. Adding a value here MUST
// come with a backend enum extension (L2 rejects anything not in this
// set with 422).
export const BONUS_SOURCES = [
  { value: 'CUSTOMER_COMPLAINT', label: 'Customer complaint' },
  { value: 'PROMOTION',          label: 'Promotion' },
  { value: 'VIP',                label: 'VIP customer' },
  { value: 'GOODWILL',           label: 'Goodwill' },
  { value: 'MANUAL_CORRECTION',  label: 'Manual correction' },
  { value: 'MARKETING_EVENT',    label: 'Marketing event' },
  { value: 'SYSTEM',             label: 'System' },
  { value: 'OTHER',              label: 'Other' },
];

const uuid4 = () => {
  // Standard v4 — using crypto.randomUUID where available so we don't
  // pull in a UUID lib just for this modal. Same key is reused across
  // retries so a network blip stays idempotent (L2 D5).
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/**
 * Standalone helper: is the current user allowed to grant bonuses at
 * all? Callers use this to decide whether to render the button that
 * opens the modal — see AC-L4-5 (cashier does NOT see the button).
 */
export function canGrantBonus(user) {
  if (!user) return false;
  return user.role === ROLES.BRAND_OWNER || user.role === ROLES.SUPER_ADMIN;
}

/**
 * Props:
 *   open          – controls modal visibility
 *   onClose()     – called to dismiss
 *   mode          – 'on_transaction' | 'standalone'
 *   context       – mode-specific fields:
 *                     on_transaction: { transactionId, shortId, autoStamp, bonusStamp }
 *                     standalone:     { customerId, customerName, brandId?, brandName? }
 *   onGranted(response) – fires on 200 with the raw L2 response
 */
export default function BonusStampModal({
  open, onClose, mode, context, onGranted,
}) {
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);

  // idempotency_key regenerates on modal open — stays stable across
  // failed retries within the same modal session (D5).
  const [idempotencyKey, setIdempotencyKey] = useState(() => uuid4());
  const [amount, setAmount] = useState('');
  const [source, setSource] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [serverError, setServerError] = useState(null);

  useEffect(() => {
    if (open) {
      setIdempotencyKey(uuid4());
      setAmount('');
      setSource('');
      setReason('');
      setNotes('');
      setServerError(null);
    }
  }, [open]);

  const amountInt = Number.parseInt(amount, 10);
  const reasonTrimmed = reason.trim();
  const canSubmit = Number.isFinite(amountInt) && amountInt > 0
                    && !!source && reasonTrimmed.length > 0;

  const mutation = useMutation({
    mutationFn: () => {
      const body = {
        idempotency_key: idempotencyKey,
        amount:          amountInt,
        source,
        reason:          reasonTrimmed,
      };
      if (notes.trim()) body.notes = notes.trim();
      if (mode === 'on_transaction') {
        return api.ownerGrantBonusOnTxn(token, context.transactionId, body);
      }
      // standalone — SA needs ?brand_id=; owner may omit.
      const brandId = user?.role === ROLES.SUPER_ADMIN ? context.brandId : null;
      return api.ownerGrantStandaloneBonus(token, context.customerId, body, brandId);
    },
    onSuccess: (response) => {
      setServerError(null);
      toast({
        title: mode === 'on_transaction'
          ? 'Bonus stamp granted'
          : 'Standalone bonus granted',
        description: mode === 'on_transaction'
          ? `+${amountInt} bonus stamp${amountInt === 1 ? '' : 's'} added — new total ${response?.final_stamp ?? amountInt}.`
          : `+${amountInt} bonus stamp${amountInt === 1 ? '' : 's'} granted to ${context?.customerName ?? 'customer'}.`,
      });
      onGranted?.(response);
      onClose?.();
    },
    onError: (e) => {
      const code = e?.raw?.detail?.code;
      const msg = e?.raw?.detail?.message
        || (typeof e?.detail === 'string' ? e.detail : null)
        || 'Could not grant bonus stamp.';
      if (code === 'bonus_stamp_forbidden') {
        setServerError('Only Brand Owner or Super Admin may grant Bonus Stamps.');
      } else if (code === 'bonus_duplicate_rapid') {
        setServerError('Duplicate detected — please wait a moment before retrying.');
      } else if (code === 'cannot_bonus_a_bonus') {
        setServerError('Cannot grant a bonus on a bonus transaction.');
      } else if (code === 'cross_brand_forbidden') {
        setServerError("This transaction belongs to a different brand.");
      } else {
        setServerError(msg);
      }
    },
  });

  const previewTotal = useMemo(() => {
    if (mode !== 'on_transaction' || !Number.isFinite(amountInt) || amountInt <= 0) return null;
    const auto = Number(context?.autoStamp || 0);
    const currentBonus = Number(context?.bonusStamp || 0);
    return auto + currentBonus + amountInt;
  }, [mode, amountInt, context?.autoStamp, context?.bonusStamp]);

  // Defensive RBAC — the button that opens us already hides for
  // cashier/GM/customer, but this guard prevents accidental render
  // from a future call-site oversight.
  if (open && !canGrantBonus(user)) {
    return (
      <Modal open={open} onClose={onClose} title="Not permitted" size="sm" testID="bonus-modal-forbidden">
        <p className="text-sm text-white/70">
          Only Brand Owner or Super Admin may grant Bonus Stamps.
        </p>
      </Modal>
    );
  }

  const modalTitle = mode === 'on_transaction'
    ? `Adjust Bonus Stamp for Transaction #${context?.shortId ?? ''}`
    : `Grant Standalone Bonus${context?.customerName ? ` to ${context.customerName}` : ''}`;

  const handleAmountChange = useCallback((e) => {
    const v = e.target.value;
    // Coerce to positive int-only input, allowing empty for backspace.
    if (v === '' || /^[0-9]+$/.test(v)) setAmount(v);
  }, []);

  return (
    <Modal
      open={open}
      onClose={mutation.isPending ? undefined : onClose}
      title={modalTitle}
      subtitle={mode === 'on_transaction'
        ? 'Additive — auto stamps stay untouched. Immutable and audit-logged.'
        : 'Creates a standalone bonus transaction. No purchase, no receipt.'}
      size="md"
      testID={mode === 'on_transaction' ? 'bonus-modal-txn' : 'bonus-modal-standalone'}
      footer={(
        <>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={mutation.isPending}
            testID="bonus-modal-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => { setServerError(null); mutation.mutate(); }}
            disabled={!canSubmit || mutation.isPending}
            loading={mutation.isPending}
            testID="bonus-modal-confirm"
          >
            Confirm &amp; Grant Bonus
          </Button>
        </>
      )}
    >
      <div className="space-y-5">
        {/* Auto stamp lock indicator — always visible to reinforce D6. */}
        {mode === 'on_transaction' && context?.autoStamp != null ? (
          <div className="flex items-center gap-3 rounded-xl border border-white/8 bg-black/30 px-4 py-3">
            <div className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 grid place-items-center text-white/60">
              <Lock size={14} />
            </div>
            <div className="text-xs leading-tight">
              <div className="text-white/85">
                Auto Stamp is <span className="text-white">{context.autoStamp}</span> (server-computed, read-only).
              </div>
              <div className="text-white/45 mt-0.5">
                Bonus adds on top — the engine math is never edited.
              </div>
            </div>
          </div>
        ) : null}

        <FieldRow label="Amount" required hint="Positive integer">
          <Input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={amount}
            onChange={handleAmountChange}
            placeholder="e.g. 3"
            data-testid="bonus-modal-amount"
          />
        </FieldRow>

        <FieldRow label="Source" required>
          <Select
            value={source}
            onChange={(e) => setSource(e.target.value)}
            data-testid="bonus-modal-source"
          >
            <option value="" disabled>Select a source…</option>
            {BONUS_SOURCES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </Select>
        </FieldRow>

        <FieldRow label="Reason" required hint="Shown in the audit log">
          <Input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={500}
            placeholder="e.g. Long-time VIP customer"
            data-testid="bonus-modal-reason"
          />
        </FieldRow>

        <FieldRow label="Notes" hint="Optional · max 1000 chars">
          <Textarea
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={1000}
            placeholder="Ticket # or context…"
            data-testid="bonus-modal-notes"
          />
        </FieldRow>

        {mode === 'on_transaction' && previewTotal != null ? (
          <div className="flex items-start gap-2 rounded-xl border border-gold/25 bg-gold/8 px-4 py-3" data-testid="bonus-modal-preview">
            <Sparkles size={14} className="text-gold mt-0.5 shrink-0" />
            <div className="text-xs text-white/85 leading-relaxed">
              <span className="text-white">+{amountInt}</span> bonus stamps → new final total{' '}
              <span className="text-gold font-semibold">{previewTotal}</span>
              <span className="text-white/45"> · immutable · audit-logged</span>
            </div>
          </div>
        ) : null}

        {mode === 'standalone' ? (
          <div className="flex items-start gap-2 rounded-xl border border-white/8 bg-black/30 px-4 py-3">
            <Info size={14} className="text-white/50 mt-0.5 shrink-0" />
            <div className="text-xs text-white/70 leading-relaxed">
              This grants <span className="text-white">{Number.isFinite(amountInt) && amountInt > 0 ? amountInt : 'N'}</span> bonus stamps as a{' '}
              new <span className="text-gold">BONUS-type transaction</span>. No purchase, no receipt, no revenue effect.
            </div>
          </div>
        ) : null}

        {serverError ? (
          <div
            className="rounded-xl border border-red-500/40 bg-red-500/5 px-4 py-3 text-xs text-red-300"
            data-testid="bonus-modal-error"
          >
            {serverError}
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
